import { z } from 'zod';

import { parseEnvBool } from './lib/envBool.js';

/**
 * Strict env schema для @veylme/miniapp.
 *
 * Парсится один раз на bootstrap. Любое нарушение → throw на старте,
 * приложение не должно загружаться с битыми переменными (CANON SP1 fail-fast).
 *
 * Переменные:
 * - `VITE_API_BASE_URL` — base URL к `@veylme/api` без trailing slash
 *   (например `http://localhost:3000`). Должен быть валидный URL.
 * - `VITE_USE_TG_MOCK` — если `true`, в `main.tsx` будет вызван
 *   `mockTelegramEnv` перед `initSDK()` (для local dev без Telegram-клиента).
 *   Истиной считаются только `true`/`1`; `false`/`0`/пусто/прочее — ложь.
 * - `VITE_BOT_USERNAME` — **необязательная** (work-038): username бота без `@`
 *   (на проде `veylme_bot`, то же значение, что серверный `BOT_USERNAME`).
 *   Нужна экрану истёкшей сессии (`screens/SessionExpiredScreen.tsx`), чтобы
 *   построить ссылку возврата `https://t.me/<bot>?startapp=<сохранённый контекст>`.
 *   Не задана → кнопка возврата деградирует до закрытия мини-аппа
 *   (`closeMiniAppSafe`); ни сборка, ни рантайм не ломаются.
 */
// Vite отдаёт незаданную переменную как undefined, но `KEY=` в .env придёт как "".
// `.optional()` пропускает только undefined → пре-процессим пустую строку в undefined.
const emptyToUndefined = (val: unknown): unknown =>
  typeof val === 'string' && val.trim() === '' ? undefined : val;

// Булев флаг: parseEnvBool (lib/envBool.ts) — истина только "true"/"1".
// preprocess всегда отдаёт boolean → z.boolean() проходит, default не нужен.
const envBool = z.preprocess(parseEnvBool, z.boolean());

const EnvSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  VITE_USE_TG_MOCK: envBool,
  // mini-watch (свой мини-Sentry, INFRA.md). Опциональны: фронт-ловушка стартует
  // только если заданы ОБА (key + url), иначе спит. Прод-.env заполняет владелец.
  VITE_MW_KEY: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_MW_URL: z.preprocess(emptyToUndefined, z.string().url().optional()),
  // work-038: username бота для ссылки возврата с экрана истёкшей сессии.
  // Необязательна — не задана, значит кнопка закрывает мини-апп вместо
  // открытия `t.me`-ссылки (см. докстринг выше).
  VITE_BOT_USERNAME: z.preprocess(emptyToUndefined, z.string().min(1).optional()),
});

export type Env = z.infer<typeof EnvSchema>;

function parseEnv(): Env {
  const parsed = EnvSchema.safeParse({
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_USE_TG_MOCK: import.meta.env.VITE_USE_TG_MOCK,
    VITE_MW_KEY: import.meta.env.VITE_MW_KEY,
    VITE_MW_URL: import.meta.env.VITE_MW_URL,
    VITE_BOT_USERNAME: import.meta.env.VITE_BOT_USERNAME,
  });
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('\n');
    throw new Error(
      `[@veylme/miniapp] Invalid env vars:\n${issues}\n` +
        `Provide valid VITE_API_BASE_URL (and optional VITE_USE_TG_MOCK) in .env.local or .env.`,
    );
  }
  // CANON SP1 fail-fast: mock initData с fake hash/signature НИКОГДА не должен
  // попасть в production-bundle. Если кто-то случайно соберёт prod с VITE_USE_TG_MOCK=1
  // — приложение упадёт на старте, лучше чем тихо отправлять fake initData на сервер.
  if (import.meta.env.PROD && parsed.data.VITE_USE_TG_MOCK) {
    throw new Error(
      '[@veylme/miniapp] VITE_USE_TG_MOCK не должен быть включён в production-сборке',
    );
  }
  return parsed.data;
}

export const env = parseEnv();
